/**
 * הכנת תמונה להעלאה: מקטינה צלע ארוכה ל-1600px וממירה ל-JPEG. תמונת טלפון רגילה (4000×3000, 3–6MB) הופכת לכמה מאות KB —
 * העלאה מהירה גם בקליטה חלשה, בדיקת התמונה האוטומטית עובדת על קובץ קטן (מגבלת הגודל שלה), וקובץ בפורמט שהשרת לא מכיר
 * (למשל HEIC מאייפון/סמסונג, שהדפדפן כן יודע לפענח) מומר לפורמט שנתמך. כל כשל בדרך = מחזירים את הקובץ המקורי כמו שהוא.
 */

export const MAX_UPLOAD_EDGE = 1600;
export const UPLOAD_JPEG_QUALITY = 0.85;
/** תמונה שכבר קטנה ממידה זו (ובפורמט תקין) נשלחת כמו שהיא — אין טעם לאבד איכות */
const SMALL_ENOUGH_BYTES = 450_000;
const DECODE_TIMEOUT_MS = 12_000;

export interface Size {
  width: number;
  height: number;
}

/** הגדלים אחרי הקטנה כך שהצלע הארוכה לא תעלה על maxEdge (בלי להגדיל תמונה קטנה) */
export function fitWithin(width: number, height: number, maxEdge: number): Size {
  const longest = Math.max(width, height);
  if (longest <= maxEdge || longest <= 0) return { width, height };
  const scale = maxEdge / longest;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    const timer = window.setTimeout(() => {
      URL.revokeObjectURL(url);
      reject(new Error('decode timeout'));
    }, DECODE_TIMEOUT_MS);
    img.onload = () => {
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      reject(new Error('decode failed'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), type, quality);
    } catch {
      resolve(null);
    }
  });
}

export async function prepareImageForUpload(
  blob: Blob,
  opts: { maxEdge?: number; quality?: number } = {},
): Promise<Blob> {
  const maxEdge = opts.maxEdge ?? MAX_UPLOAD_EDGE;
  const quality = opts.quality ?? UPLOAD_JPEG_QUALITY;
  try {
    if (typeof document === 'undefined' || typeof Image === 'undefined') return blob;
    // GIF נשלח כמו שהוא (אנימציה); כל השאר מפוענח ומצויר מחדש
    if (blob.type === 'image/gif') return blob;

    const img = await loadImage(blob);
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) return blob;

    const target = fitWithin(width, height, maxEdge);
    const resized = target.width !== width || target.height !== height;
    // JPEG קטן שלא צריך הקטנה — נשאר כמו שהוא
    if (!resized && blob.type === 'image/jpeg' && blob.size <= SMALL_ENOUGH_BYTES) return blob;

    const canvas = document.createElement('canvas');
    canvas.width = target.width;
    canvas.height = target.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;
    // רקע לבן: PNG שקוף היה הופך לשחור ב-JPEG
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const out = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (!out || out.size === 0) return blob;
    // לא מחליפים בקובץ גדול יותר (אלא אם הפורמט המקורי לא נתמך בשרת)
    if (!resized && out.size >= blob.size && /^image\/(jpeg|png|webp)$/.test(blob.type)) return blob;
    return out;
  } catch {
    return blob;
  }
}
